import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDownload } from "../pages/DownloadWindow/DownloadContext";
import { gaEvent } from "../utils/gaAnalytics";

export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=ai.zerotyping";

export const APP_STORE_URL =
  "https://apps.apple.com/us/app/zero-typing-ai-voice-to-text/id6752406283";

// Download button background. The first color is held to a narrow strip on the
// left (roughly the icon area) before transitioning to the second color, so the
// button reads mostly as #407BFF with a lighter #3AB1F6 accent under the icon.
export const DOWNLOAD_GRADIENT =
  "linear-gradient(90deg, #3AB1F6 0%, #407BFF 50px)";

// Same installer the footer's "Download for Windows" button points at.
export const WINDOWS_INSTALLER_URL =
  process.env.REACT_APP_INSTALLER_URL ||
  "https://zerotyping.ai/ztaupdate/ZeroTypingAI.msi";

// Triggers the browser's native download of the .msi, exactly like the footer's
// <a href> anchor does (starts the real download; not the streaming fetch).
const triggerWindowsDownload = () => {
  const a = document.createElement("a");
  a.href = WINDOWS_INSTALLER_URL;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/**
 * Detects the visitor's operating system from the user agent.
 * Returns one of: "windows" | "android" | "ios" | "mac" | "unknown".
 */
export const detectOS = () => {
  if (typeof navigator === "undefined") return "unknown";
  const ua = (navigator.userAgent || "").toLowerCase();
  const platform = (navigator.platform || "").toLowerCase();

  // Android must be checked before "linux"/"mac" since some UAs include those.
  if (ua.includes("android")) return "android";

  // iOS: iPhone/iPad/iPod. iPadOS 13+ masquerades as Mac, so also detect a
  // touch-capable "MacIntel" as iOS.
  if (
    /iphone|ipad|ipod/.test(ua) ||
    (platform === "macintel" && navigator.maxTouchPoints > 1)
  ) {
    return "ios";
  }

  if (ua.includes("win")) return "windows";
  if (ua.includes("mac")) return "mac";

  return "unknown";
};

// Flip to true once the macOS build ships. While it is false every Mac-facing
// CTA reads "Coming Soon on Mac" and is rendered non-interactive.
export const MAC_BUILD_LIVE = false;

// True when the visitor is on a Mac and the Mac build isn't available yet, so
// the OS-aware buttons should be disabled rather than routed anywhere.
export const isMacComingSoon = () => detectOS() === "mac" && !MAC_BUILD_LIVE;

// The macOS build isn't shipped yet, so every Mac-facing CTA advertises it as
// coming soon instead of promising a download.
export const MAC_COMING_SOON_LABEL = "Coming Soon on Mac";

// Compact variant for tight layouts (mobile navbar).
export const MAC_COMING_SOON_LABEL_SHORT = "Coming Soon";

/**
 * OS-aware label for a download button, e.g. "Download for Windows".
 * Falls back to "Download App" when the OS can't be determined.
 */
export const getDownloadLabel = () => {
  switch (detectOS()) {
    case "windows":
      return "Download for Windows";
    case "mac":
      return MAC_COMING_SOON_LABEL;
    case "ios":
      return "Download for iPhone";
    case "android":
      return "Download for Android";
    default:
      return "Download App";
  }
};

/**
 * Returns a click handler for "Download App" buttons that adapts to the
 * visitor's OS instead of always routing to the /download page:
 *   - windows  -> start the .msi download + show the Windows install modal
 *                 (identical to the footer's "Download for Windows" button)
 *   - android  -> open the Google Play listing in a new tab
 *   - mac/ios  -> route to the /download page (native builds not wired yet)
 *   - unknown  -> route to the /download page (safe fallback)
 *
 * `location` is an optional label forwarded to analytics (e.g. "home_hero").
 */
export const useSmartDownload = () => {
  const download = useDownload();
  const navigate = useNavigate();

  return useCallback(
    (location = "unknown") => {
      const os = detectOS();

      // The Mac build isn't out yet: its CTAs are disabled, and this guard
      // keeps any that slip through from navigating anywhere.
      if (os === "mac" && !MAC_BUILD_LIVE) return;

      switch (os) {
        case "windows":
          gaEvent("download_click", {
            platform: "windows",
            file_name: "ZeroTypingAI.msi",
            location,
          });
          // Start the real browser download, then show the modal — every click,
          // exactly like the footer's "Download for Windows" button.
          triggerWindowsDownload();
          download?.openInstallModal("windows");
          break;

        case "android":
          gaEvent("download_click", { platform: "android", location });
          window.open(PLAY_STORE_URL, "_blank", "noopener,noreferrer");
          break;

        case "ios":
          gaEvent("download_click", { platform: "ios", location });
          window.open(APP_STORE_URL, "_blank", "noopener,noreferrer");
          break;

        case "mac":
        default:
          gaEvent("download_click", { platform: os, location });
          navigate("/download");
          break;
      }
    },
    [download, navigate]
  );
};
